<template>
  <DocLayout :doc="doc">
    <template v-if="doc.showEarlyAccessBanner" #banner>
      <EarlyAccessBannerBook v-if="showBookBanner" />
      <EarlyAccessBannerPodcast v-if="doc.doc_type === 'podcast_episode'" />
    </template>
    <template #page-content>
      <div
        class="book-reading__content-editor content-editor"
        :class="doc.settings.cssClass"
        :style="{ 'font-family': doc.settings.fontFamily }"
        @click="onDocContentClick"
      >
        <Chapter
          v-if="doc.isCurrentPageChapter"
          :content="doc.currentPage"
          class="chapter-text"
        />
        <Exercise
          v-else-if="doc.isCurrentPageOpenQuestions"
          :content="doc.currentExercise"
          :isEOC="doc.currentExercise.currentExercise.isExerciseEOC"
          class="chapter-exercise"
        />
      </div>
      <PrevNext :doc="doc" />
    </template>
    <template #page-footer>
      <AudioPlayer
        v-if="doc.audio && doc.controls.isPlayerRendered"
        :doc="doc"
      />
      <AudioPlayerDisabled
        v-if="!doc.audio && doc.has_audio && doc.controls.isPlayerRendered"
        :class="doc.controls.playerClass"
      />
    </template>
  </DocLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeMount } from 'vue'

import DocLayout from '@/app/components/doc/Layout.vue'
import PrevNext from '@/app/components/doc/PrevNext.vue'
import Chapter from '@/app/components/doc/reading/Chapter.vue'
import Exercise from '@/app/components/doc/reading/Exercise.vue'
import EarlyAccessBannerBook from '@/app/components/doc/EarlyAccessBannerBook.vue'
import EarlyAccessBannerPodcast from '@/app/components/doc/EarlyAccessBannerPodcast.vue'

import AudioPlayer from '@/app/components/doc/player/Player.vue'
import AudioPlayerDisabled from '@/app/components/doc/player/PlayerDisabled.vue'

import { emitter } from '@/services/mitt'
import { DocView } from '@/models/doc'
import { AnnotationWithContent } from '@/models/interfaces'
import { backend } from '@/services/backend'
import {
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  useRoute,
} from 'vue-router'
import { Sentry } from '@/services/sentry'
import { aiLabel13537ExperimentVariant } from '@/services/ab'
import { Mixpanel } from '@/analytics/mixpanel'

const props = defineProps<{
  doc: DocView
}>()

const route = useRoute()

onBeforeMount(async () => {
  await handleHighlight(route)
  Mixpanel.setFirstDoc('Viewed', props.doc.title, props.doc.doc_type)
})

onBeforeRouteUpdate(async (to: RouteLocationNormalized) => {
  await handleHighlight(to)
})

function onDocContentClick(event: any): void {
  emitter.emit('docContentClicked', event)
}

/**
 * Fetch a highlight by ID if the `highlight` query param is present.
 */
async function _maybeFetchHighlight(
  route: RouteLocationNormalized,
): Promise<AnnotationWithContent | undefined> {
  const highlightId = route.query?.highlight as string | undefined
  if (!highlightId) {
    return undefined
  }
  try {
    return await backend.getHighlight(highlightId)
  } catch (e) {
    Sentry.captureException(e)
    return undefined
  }
}

const showBookBanner = computed(() => {
  return props.doc.doc_type === 'book' && aiLabel13537ExperimentVariant()
})

async function handleHighlight(route: RouteLocationNormalized): Promise<void> {
  const highlight = await _maybeFetchHighlight(route)
  if (highlight) {
    props.doc.scrollToHighlight(highlight)
  }
}
</script>
